<script setup>
import { ref, computed } from 'vue'
import { useProjectionsStore } from '../stores/projections'

const props = defineProps({
  fixtureId: {
    type: [String, Number],
    required: true
  },
  fileName: {
    type: String,
    default: 'balanced-lines-data'
  }
})

const projectionsStore = useProjectionsStore()
const isDownloading = ref(false)
const showTooltip = ref(false)
const showPopup = ref(false)
const downloadType = ref('single') // 'single' or 'multiple'

// Computed property to check if there's data to download
const hasData = computed(() => {
  return projectionsStore.balancedLinesProbabilitiesAndOdds && 
         projectionsStore.balancedLinesProbabilitiesAndOdds.length > 0 &&
         projectionsStore.balancedLinesData &&
         (projectionsStore.balancedLinesData.home_team?.length > 0 || 
          projectionsStore.balancedLinesData.away_team?.length > 0)
})

// Function to convert data to CSV format
const convertToCSV = (data, marketType = null) => {
  if (!data || data.length === 0) return ''
  
  // Filter data by market type if specified and exclude suspended items
  const filteredData = (marketType ? data.filter(item => item.market_type === marketType) : data)
    .filter(item => !item.is_suspended)
  
  // Get player data from balancedLinesData to map player names
  const homeTeamPlayers = projectionsStore.balancedLinesData?.home_team || []
  const awayTeamPlayers = projectionsStore.balancedLinesData?.away_team || []
  
  // Create a map of player_id to player info
  const playerMap = new Map()
  
  // Map home team players
  homeTeamPlayers.forEach(player => {
    playerMap.set(player.player_id, {
      name: player.player?.player_name || 'Unknown Player',
      team: player.home_team_name?.team_name || 'Home Team'
    })
  })
  
  // Map away team players
  awayTeamPlayers.forEach(player => {
    playerMap.set(player.player_id, {
      name: player.player?.player_name || 'Unknown Player',
      team: player.away_team_name?.team_name || 'Away Team'
    })
  })
  
  // Define headers based on the data structure
  const headers = [
    'Player Name',
    'Team',
    'Market Type',
    'Balance Line',
    'Over Odds',
    'Under Odds',
    'Is Suspended'
  ]
  
  // Convert data to CSV rows
  const rows = filteredData.map(item => {
    const playerInfo = playerMap.get(item.player_id) || { name: 'Unknown Player', team: 'Unknown Team' }
    
    return [
      playerInfo.name,
      playerInfo.team,
      item.market_type || '',
      item.balance_line ? item.balance_line.toFixed(1) : '',
      item.balance_line_over_odds || '',
      item.balance_line_under_odds || '',
      item.is_suspended ? 'Yes' : 'No'
    ]
  })
  
  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n')
  
  return csvContent
}

// Function to get unique market types
const getUniqueMarketTypes = (data) => {
  if (!data || data.length === 0) return []
  return [...new Set(data.map(item => item.market_type).filter(Boolean))]
}

// Function to convert data to sheet format for Excel
const convertToSheetData = (data, marketType = null) => {
  if (!data || data.length === 0) return []
  
  // Filter data by market type if specified and exclude suspended items
  const filteredData = (marketType ? data.filter(item => item.market_type === marketType) : data)
    .filter(item => !item.is_suspended)
  
  // Get player data from balancedLinesData to map player names
  const homeTeamPlayers = projectionsStore.balancedLinesData?.home_team || []
  const awayTeamPlayers = projectionsStore.balancedLinesData?.away_team || []
  
  // Create a map of player_id to player info
  const playerMap = new Map()
  
  // Map home team players
  homeTeamPlayers.forEach(player => {
    playerMap.set(player.player_id, {
      name: player.player?.player_name || 'Unknown Player',
      team: player.home_team_name?.team_name || 'Home Team'
    })
  })
  
  // Map away team players
  awayTeamPlayers.forEach(player => {
    playerMap.set(player.player_id, {
      name: player.player?.player_name || 'Unknown Player',
      team: player.away_team_name?.team_name || 'Away Team'
    })
  })
  
  // Convert data to sheet format
  return filteredData.map(item => {
    const playerInfo = playerMap.get(item.player_id) || { name: 'Unknown Player', team: 'Unknown Team' }
    
    return {
      'Player Name': playerInfo.name,
      'Team': playerInfo.team,
      'Market Type': item.market_type || '',
      'Balance Line': item.balance_line ? item.balance_line.toFixed(1) : '',
      'Over Odds': item.balance_line_over_odds || '',
      'Under Odds': item.balance_line_under_odds || '',
      'Is Suspended': item.is_suspended ? 'Yes' : 'No'
    }
  })
}

// Function to download CSV/Excel
const downloadCSV = async () => {
  if (!hasData.value) {
    console.warn('No data available for download')
    return
  }
  
  isDownloading.value = true
  
  try {
    // Get the balanced lines data
    const data = projectionsStore.balancedLinesProbabilitiesAndOdds
    
    if (downloadType.value === 'single') {
      // Single sheet download (CSV)
      const csvContent = convertToCSV(data)
      downloadFile(csvContent, `${props.fileName}-${props.fixtureId}-${new Date().toISOString().split('T')[0]}.csv`)
    } else {
      // Multiple sheets download (Excel)
      const marketTypes = getUniqueMarketTypes(data)
      
      if (marketTypes.length === 0) {
        console.warn('No market types found')
        return
      }
      
      // Create Excel workbook with multiple sheets
      const XLSX = await import('xlsx')
      const workbook = XLSX.utils.book_new()
      
      marketTypes.forEach(marketType => {
        const sheetData = convertToSheetData(data, marketType)
        if (sheetData.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(sheetData)
          XLSX.utils.book_append_sheet(workbook, worksheet, marketType)
        }
      })
      
      // Generate and download Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadFile(excelBlob, `${props.fileName}-${props.fixtureId}-${new Date().toISOString().split('T')[0]}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    }
  } catch (error) {
    console.error('Error downloading file:', error)
  } finally {
    isDownloading.value = false
    showPopup.value = false
  }
}

// Helper function to download file
const downloadFile = (content, fileName, mimeType = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mimeType })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

// Function to handle mouse enter for tooltip
const handleMouseEnter = () => {
  showTooltip.value = true
}

// Function to handle mouse leave for tooltip
const handleMouseLeave = () => {
  showTooltip.value = false
}

// Function to handle button click
const handleButtonClick = () => {
  if (hasData.value && !isDownloading.value) {
    showPopup.value = !showPopup.value
  }
}

// Function to handle download type selection
const selectDownloadType = (type) => {
  downloadType.value = type
  downloadCSV()
}

// Function to close popup
const closePopup = () => {
  showPopup.value = false
}

// Handle click outside to close popup
const handleClickOutside = (event) => {
  if (showPopup.value && !event.target.closest('.download-button-container')) {
    showPopup.value = false
  }
}

// Add and remove event listener
import { onMounted, onUnmounted } from 'vue'

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="fixed bottom-6 right-6 z-50 download-button-container">
    <!-- Floating Download Button -->
    <button
      @click="handleButtonClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      :disabled="!hasData || isDownloading"
      class="relative group"
    >
      <!-- Main Button -->
      <div class="w-14 h-14 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-white">
        <i 
          v-if="!isDownloading" 
          class="fas fa-download text-lg group-hover:scale-110 transition-transform duration-200"
        ></i>
        <i 
          v-else 
          class="fas fa-spinner fa-spin text-lg"
        ></i>
      </div>
      
      <!-- Pulse Animation Ring -->
      <div class="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20"></div>
      
      <!-- Tooltip -->
      <div 
        v-if="showTooltip"
        class="absolute bottom-full right-0 mb-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap"
      >
        <div class="flex items-center gap-2">
          <i class="fas fa-file-csv text-blue-400"></i>
          <span>{{ 
            isDownloading ? 'Downloading...' : 
            !hasData ? 'No data available' : 
            'Download Balanced Lines Data' 
          }}</span>
        </div>
        
        <!-- Tooltip Arrow -->
        <div class="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
      
      <!-- No Data Indicator -->
      <div 
        v-if="!hasData"
        class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
      >
        <i class="fas fa-exclamation text-white text-xs"></i>
      </div>
    </button>
    
    <!-- Download Options Popup -->
    <div 
      v-if="showPopup"
      class="absolute bottom-full right-0 mb-3 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[200px]"
    >
      <!-- Popup Header -->
      <div class="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-gray-900">Download Options</h3>
          <button 
            @click="closePopup"
            class="text-gray-400 hover:text-gray-600"
          >
            <i class="fas fa-times text-xs"></i>
          </button>
        </div>
      </div>
      
      <!-- Popup Content -->
      <div class="py-2">
        <!-- Single Sheet Option -->
        <button
          @click="selectDownloadType('single')"
          class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
        >
          <div class="w-4 h-4 rounded border-2 border-gray-300 flex items-center justify-center">
            <div v-if="downloadType === 'single'" class="w-2 h-2 bg-blue-500 rounded-full"></div>
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900">Single Sheet</div>
            <div class="text-xs text-gray-500">All markets in one CSV file</div>
          </div>
        </button>
        
        <!-- Multiple Sheets Option -->
        <button
          @click="selectDownloadType('multiple')"
          class="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
        >
          <div class="w-4 h-4 rounded border-2 border-gray-300 flex items-center justify-center">
            <div v-if="downloadType === 'multiple'" class="w-2 h-2 bg-blue-500 rounded-full"></div>
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900">Multiple Sheets</div>
            <div class="text-xs text-gray-500">Excel file with separate sheet for each market</div>
          </div>
        </button>
      </div>
      
      <!-- Popup Arrow -->
      <div class="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
    </div>
  </div>
</template>

<style scoped>
/* Custom animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.1;
  }
}

.animate-ping {
  animation: pulse 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

/* Hover effects */
.group:hover .animate-ping {
  animation-duration: 1s;
}

/* Disabled state */
button:disabled {
  cursor: not-allowed;
}

button:disabled .animate-ping {
  animation: none;
}

/* Tooltip animations */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
}
</style>
